import axios from 'axios';
import promise from 'redux-promise-middleware';
import ReduxThunk from 'redux-thunk';
import { createLogger } from 'redux-logger';
import { createStore, applyMiddleware, compose } from 'redux';
import { routerMiddleware } from 'connected-react-router';
import clientMiddleware from './Middlewares/ClientMiddleware';
import history from '../Router/History';
import RootReducer from './Reducers/RootReducer';
import { readRecord } from '../Services/LocalStorageService';
import { getTenantCode } from '../Utils/tenantUtils';
import refreshAccessToken from '../Services/RefreshTokenService';
import { LS_KEY_AUTH_DATA } from '../constant/constants';

export const getHeader = (tokenId, provider) => {
  const headers = { 'X-Tenant-Code': getTenantCode() };
  if (tokenId) {
    headers.Authorization = tokenId;
  }
  if (provider) {
    headers['X-Auth-Provider'] = provider === 'google' ? 'google-sso' : 'basic-auth';
  }
  return { headers };
};

const httpClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json'
  }
});

httpClient.interceptors.request.use(config => {
  if (!config.headers['X-Tenant-Code']) {
    config.headers['X-Tenant-Code'] = getTenantCode();
  }

  const authData = readRecord(LS_KEY_AUTH_DATA);
  if (authData) {
    if (!config.headers.Authorization) {
      config.headers.Authorization = authData.token_id;
    }
    if (!config.headers['X-Auth-Provider']) {
      config.headers['X-Auth-Provider'] =
        authData.provider === 'google' ? 'google-sso' : 'basic-auth';
    }
  }
  return config;
});

// Refresh token on 401 error
httpClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('login')
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = newToken;
        return httpClient(originalRequest);
      } catch (refreshError) {
        console.error('Session expired. Redirecting to login.');
        localStorage.removeItem(LS_KEY_AUTH_DATA);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

const configureStore = (options = { logger: true }) => {
  const enhancers = [];

  if (process.env.REACT_APP_ENV === 'Development') {
    const { devToolsExtension } = window;

    if (typeof devToolsExtension === 'function') {
      enhancers.push(devToolsExtension());
    }
  }

  const middleware = [ReduxThunk, routerMiddleware(history), promise, clientMiddleware(httpClient)];
  if (options.logger && process.env.REACT_APP_ENV !== 'Production') {
    const logger = createLogger({ collapsed: true });
    middleware.push(logger);
  }
  const composedEnhancers = compose(applyMiddleware(...middleware), ...enhancers);

  // Create the store
  const store = createStore(RootReducer, composedEnhancers);

  return {
    store,
    history,
    middleware
  };
};

export default configureStore;
